import { useCallback, useEffect, useState } from "react";
import { Mail, RefreshCw, Loader2, Send } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

const STATUS_STYLE = {
  sent: "bg-emerald-500/10 text-emerald-300",
  queued: "bg-blue-500/10 text-blue-300",
  sending: "bg-blue-500/10 text-blue-300",
  failed: "bg-red-500/10 text-red-300",
  skipped: "bg-white/5 text-white/40",
  mock: "bg-amber-500/10 text-amber-300",
};

const when = (v) => (v ? String(v).replace("T", " ").slice(0, 16) : "—");

/** Platform IT Operations: raw email delivery logs, retry queue and provider message ids. */
export default function AdminEmailActivity({ adminApi, clinics = [] }) {
  const [logs, setLogs] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [stats, setStats] = useState(null);
  const [provider, setProvider] = useState(null);
  const [suppressions, setSuppressions] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState("");
  const [testing, setTesting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [clinicFilter, setClinicFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retrying, setRetrying] = useState(null);
  const [feedback, setFeedback] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const q = new URLSearchParams({ limit: "50" });
    if (statusFilter) q.append("status", statusFilter);
    if (clinicFilter) q.append("clinic_id", clinicFilter);
    const oq = new URLSearchParams({ limit: "25" });
    if (clinicFilter) oq.append("clinic_id", clinicFilter);
    try {
      const [logsRes, outboxRes, statsRes, providerRes, suppRes] = await Promise.all([
        adminApi("get", `/email-logs?${q}`),
        adminApi("get", `/email-logs/outbox?${oq}`),
        adminApi("get", `/email-logs/stats${clinicFilter ? `?clinic_id=${clinicFilter}` : ""}`),
        adminApi("get", "/email-logs/provider-status"),
        adminApi("get", "/email-logs/suppressions?limit=50"),
      ]);
      setLogs(logsRes.data.logs || []);
      setOutbox(outboxRes.data.items || []);
      setStats(statsRes.data);
      setProvider(providerRes.data);
      setSuppressions(suppRes.data.suppressions || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load email activity");
    }
    setLoading(false);
  }, [adminApi, statusFilter, clinicFilter]);

  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    setTesting(true);
    setTestResult("");
    try {
      const { data } = await adminApi("post", "/email-logs/test-send", { to: testTo.trim() });
      setTestResult(data.status === "sent"
        ? `Test email accepted by the provider (message id ${data.message_id || "n/a"}). Check the inbox, sender and Reply-To.`
        : `Not sent: ${data.error || data.status}`);
      load();
    } catch (e) {
      setTestResult(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Test send failed");
    }
    setTesting(false);
  };

  const removeSuppression = async (email) => {
    try {
      await adminApi("delete", `/email-logs/suppressions/${encodeURIComponent(email)}`);
      setFeedback(`${email} can receive email again`);
      load();
    } catch {
      setFeedback("Could not clear that address");
    }
  };

  const retry = async (id) => {
    setRetrying(id);
    setFeedback("");
    try {
      const { data } = await adminApi("post", `/email-logs/outbox/${id}/retry`);
      setFeedback(data.message || "Retry attempted");
      load();
    } catch (e) {
      setFeedback(e.response?.data?.detail || "Retry failed");
    }
    setRetrying(null);
  };

  return (
    <div className="mt-8" data-testid="admin-email-activity">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-medium text-white flex items-center gap-2" style={H}>
            <Mail className="w-4 h-4 text-white/40" /> Email activity
          </h2>
          <p className="text-xs text-white/40 mt-0.5">
            Provider delivery status, message ids and the retry queue across all clinics
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={clinicFilter} onChange={(e) => setClinicFilter(e.target.value)}
            data-testid="admin-email-clinic-filter"
            className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 text-xs">
            <option value="">All clinics</option>
            {clinics.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="admin-email-status-filter"
            className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 text-xs">
            <option value="">All statuses</option>
            {["sent", "queued", "failed", "skipped", "mock"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={load} disabled={loading} data-testid="admin-email-refresh-btn"
            className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 text-xs hover:bg-white/10 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 mb-4 text-xs text-red-300"
          data-testid="admin-email-error">{error}</div>
      )}
      {feedback && <p className="text-xs text-white/60 mb-3" data-testid="admin-email-feedback">{feedback}</p>}

      {provider && (provider.sandbox_mode || provider.mode !== "live" || !provider.resend_key_configured) && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-5 text-xs text-amber-200"
          data-testid="admin-email-sandbox-warning">
          <p className="font-medium mb-1">This environment is not sending real email</p>
          <ul className="space-y-0.5 text-amber-200/80">
            {provider.sandbox_mode && <li>EMAIL_SANDBOX_MODE is on — only allowlisted test inboxes receive mail. Set it to false.</li>}
            {provider.mode !== "live" && <li>EMAIL_MODE is "{provider.mode}" — set it to live.</li>}
            {!provider.resend_key_configured && <li>RESEND_API_KEY is missing in this environment.</li>}
          </ul>
          <p className="mt-2 text-amber-200/60">
            Update these in the deployment Secrets tab, then republish so the live backend reloads them.
          </p>
        </div>
      )}

      {provider && (
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 mb-5" data-testid="admin-email-delivery-config">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-white/60">Sending configuration</p>
            <span className={`text-[10px] px-2 py-0.5 rounded ${provider.sender_is_own_domain ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"}`}
              data-testid="admin-email-sender-badge">
              {provider.sender_is_own_domain ? "Own verified domain" : "Platform-managed sender"}
            </span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-y-2 gap-x-6 text-xs">
            {[["Mode", provider.mode],
              ["Provider", provider.provider],
              ["From", provider.from_email],
              ["From name", provider.from_name_pattern],
              ["Reply-To fallback", provider.reply_to_fallback],
              ["Sending domain", provider.sending_domain],
              ["Resend key", provider.resend_key_configured ? "configured" : "not set"],
              ["Sandbox mode", provider.sandbox_mode ? "ON (test inboxes only)" : "off"],
              ["Patient link base", provider.frontend_base_url || "not set"],
              ["Managed fallback", provider.managed_fallback_enabled ? "ENABLED (break-glass)" : "disabled"],
              ["Bounce webhook", provider.webhook_secret_configured ? "configured" : "not set"],
              ["Suppressed addresses", provider.suppressed_addresses],
              ["Queued emails", provider.queued_emails]].map(([l, v]) => (
              <div key={l} className="flex gap-2">
                <span className="text-white/40 w-36 flex-shrink-0">{l}</span>
                <span className="text-white/70 truncate">{String(v ?? "—")}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-white/[0.06]">
            <input value={testTo} onChange={(e) => setTestTo(e.target.value)}
              placeholder="you@example.com" data-testid="admin-email-test-input"
              className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/80 text-xs min-w-[220px]" />
            <button onClick={sendTest} disabled={testing || !testTo.trim()} data-testid="admin-email-test-send-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white/80 text-xs hover:bg-white/20 disabled:opacity-40">
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send test email
            </button>
            <span className="text-[10px] text-white/30">No patient data is included in the test email.</span>
          </div>
          {testResult && <p className="text-xs text-white/60 mt-2" data-testid="admin-email-test-result">{testResult}</p>}
        </div>
      )}

      {suppressions.length > 0 && (
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 mb-5" data-testid="admin-email-suppressions">
          <p className="text-xs text-white/60 mb-3">Blocked addresses ({suppressions.length}) — bounced or reported spam</p>
          <div className="space-y-2">
            {suppressions.map((s) => (
              <div key={s.email} className="flex flex-wrap items-center gap-3 text-xs text-white/70">
                <span className="px-2 py-0.5 rounded bg-red-500/10 text-red-300">{s.reason}</span>
                <span className="truncate">{s.email}</span>
                <span className="text-white/30">{when(s.updated_at)}</span>
                <button onClick={() => removeSuppression(s.email)}
                  data-testid={`admin-email-unsuppress-${s.email}`}
                  className="ml-auto px-2 py-1 rounded bg-white/5 border border-white/10 hover:bg-white/10">
                  Allow again
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats && (        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="admin-email-stats">
          {[["Total", stats.total, "text-white"],
            ["Sent", stats.by_status?.sent ?? 0, "text-emerald-400"],
            ["Failed", stats.by_status?.failed ?? 0, "text-red-400"],
            ["Awaiting retry", outbox.filter((o) => o.status !== "sent").length, "text-blue-400"]].map(([l, v, tone]) => (
            <div key={l} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
              <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">{l}</p>
              <p className={`text-2xl font-medium ${tone}`}>{v}</p>
            </div>
          ))}
        </div>
      )}

      {outbox.length > 0 && (
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 mb-5" data-testid="admin-email-outbox">
          <p className="text-xs text-white/60 mb-3">Retry queue ({outbox.length})</p>
          <div className="space-y-2">
            {outbox.map((o) => (
              <div key={o.id} className="flex flex-wrap items-center gap-3 text-xs text-white/70">
                <span className={`px-2 py-0.5 rounded ${STATUS_STYLE[o.status] || "bg-white/5 text-white/40"}`}>{o.status}</span>
                <span className="truncate">{o.email_type}</span>
                <span className="text-white/40 truncate">{o.to_email || o.recipient_email}</span>
                <span className="text-white/30">{when(o.created_at)}</span>
                <button onClick={() => retry(o.id)} disabled={retrying === o.id}
                  data-testid={`admin-email-retry-${o.id}`}
                  className="ml-auto px-2 py-1 rounded bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-40">
                  {retrying === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Retry"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-x-auto">
        <table className="w-full text-xs" data-testid="admin-email-log-table">
          <thead>
            <tr className="text-white/40 text-left">
              {["Sent", "Type", "Recipient", "Clinic", "From", "Reply-To", "Status", "Provider id"].map((h) => (
                <th key={h} className="px-4 py-3 font-normal">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-6 text-white/40">
                {loading ? "Loading..." : "No email logs for this filter."}
              </td></tr>
            ) : logs.map((l) => (
              <tr key={l.id} className="border-t border-white/[0.05] text-white/70">
                <td className="px-4 py-2.5 whitespace-nowrap">{when(l.created_at)}</td>
                <td className="px-4 py-2.5">{l.email_type}</td>
                <td className="px-4 py-2.5 truncate max-w-[200px]">{l.recipient_email}</td>
                <td className="px-4 py-2.5 truncate max-w-[160px]">{l.clinic_name || "—"}</td>
                <td className="px-4 py-2.5 truncate max-w-[180px]" title={`${l.from_name || ""} <${l.from_email || ""}> reply-to ${l.reply_to || "—"}`}>
                  {l.from_email || "—"}
                  {l.fallback_provider_used ? <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300">fallback</span> : null}
                  {l.sandbox_blocked ? <span className="ml-2 px-1.5 py-0.5 rounded bg-white/5 text-white/40">sandbox</span> : null}
                </td>
                <td className="px-4 py-2.5 truncate max-w-[180px]" title={l.reply_to || ""}>{l.reply_to || "—"}</td>
                <td className="px-4 py-2.5">
                  <span className={`px-2 py-0.5 rounded ${STATUS_STYLE[l.status] || "bg-white/5 text-white/40"}`}>{l.status}</span>
                  {l.delivery_status ? <span className="text-white/40 ml-2">{l.delivery_status}</span> : null}
                  {l.failure_reason ? <span className="text-red-300/70 ml-2" title={l.user_message || ""}>{l.failure_reason}</span> : null}
                </td>
                <td className="px-4 py-2.5 font-mono text-white/40">{(l.provider_message_id || "—").slice(0, 14)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
